import { useState, useEffect, useCallback, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { errorToast, successToast } from "../Toasts/Toasts";
import {
  getVendors,
  getVendorThread,
  markVendorRead,
  replyToVendor,
  approveRequisition,
  cancelRequisition,
  confirmVendorRates,
  rejectVendorRates,
} from "../../services/vendorInboxService";

const baseUrl = process.env.REACT_APP_API_HOST;
const POLL_MS = 30000;
// The Zoho Vendors placeholder for replies from numbers no vendor carries.
const UNIDENTIFIED_ID = "1205037000001290033";

// One thread per vendor: requisitions we sent, replies they sent, messages
// MG typed here. Anything waiting on MG - a photo to confirm, a question
// with no rate in it, a failed send - floats to the top of the vendor list.

const STATUS_CLASS = {
  Draft: "bg-secondary",
  Sent: "bg-primary",
  Replied: "bg-success",
  Failed: "bg-danger",
  Cancelled: "bg-dark",
  Logged: "bg-success",
  Partial: "bg-warning text-dark",
  Pending: "bg-warning text-dark",
  NotARate: "bg-info text-dark",
  Error: "bg-danger",
  Ignored: "bg-secondary",
  Rejected: "bg-dark",
  Sending: "bg-secondary",
};

function Badge({ status }) {
  return <span className={`badge ${STATUS_CLASS[status] || "bg-secondary"}`}>{status}</span>;
}

function when(at) {
  if (!at) return "";
  const d = new Date(at);
  return d.toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
}

const VendorInbox = () => {
  const [vendors, setVendors] = useState([]);
  // ?vendor=<zoho id> opens that thread (the desk links here from an ask)
  const [params] = useSearchParams();
  const [selected, setSelected] = useState(params.get("vendor") || null);
  const [thread, setThread] = useState(null);
  const [loadingThread, setLoadingThread] = useState(false);
  const [reply, setReply] = useState("");
  const [sending, setSending] = useState(false);
  const bottomRef = useRef(null);

  const loadVendors = useCallback(async () => {
    try {
      setVendors((await getVendors()) || []);
    } catch (error) {
      errorToast("Could not load vendors");
    }
  }, []);

  const loadThread = useCallback(async (vendorId, { silent } = {}) => {
    if (!vendorId) return;
    try {
      if (!silent) setLoadingThread(true);
      const data = await getVendorThread(vendorId);
      setThread(data);
      const unread = (data.items || []).some((i) => i.kind === "intake" && !i.readAt);
      if (unread) {
        await markVendorRead(vendorId);
        loadVendors();
      }
    } catch (error) {
      errorToast("Could not load the conversation");
    } finally {
      if (!silent) setLoadingThread(false);
    }
  }, [loadVendors]);

  useEffect(() => {
    loadVendors();
    const timer = setInterval(() => {
      loadVendors();
      if (selected) loadThread(selected, { silent: true });
    }, POLL_MS);
    return () => clearInterval(timer);
  }, [loadVendors, loadThread, selected]);

  useEffect(() => {
    if (selected) loadThread(selected);
  }, [selected, loadThread]);

  useEffect(() => {
    if (bottomRef.current) bottomRef.current.scrollIntoView({ block: "end" });
  }, [thread]);

  const refresh = () => {
    loadVendors();
    if (selected) loadThread(selected, { silent: true });
  };

  const handleSend = async () => {
    if (!reply.trim() || !selected) return;
    try {
      setSending(true);
      await replyToVendor(selected, reply.trim());
      setReply("");
      successToast("Sent");
      refresh();
    } catch (error) {
      errorToast(error?.response?.data?.message || "Send failed");
    } finally {
      setSending(false);
    }
  };

  const act = async (fn, okMessage) => {
    try {
      await fn();
      if (okMessage) successToast(okMessage);
      refresh();
    } catch (error) {
      errorToast(error?.response?.data?.message || "Something went wrong");
    }
  };

  const current = vendors.find((v) => v.vendorId === selected);

  return (
    <div className="page-body px-0 py-lg-2 py-1 mt-0 mt-lg-3 ms-3">
      <div className="container-fluid p-0">
        <div className="row-title px-2">
          <h6 className="fw-bold">Vendor Inbox</h6>
        </div>

        <div className="row g-3 mt-1">
          {/* vendor list */}
          <div className="col-lg-4 col-xl-3">
            <div className="card">
              <div className="list-group list-group-flush" style={{ maxHeight: "75vh", overflowY: "auto" }}>
                {vendors.length === 0 && <div className="p-3 text-muted">No vendors yet</div>}
                {vendors.map((v) => (
                  <button
                    key={v.vendorId}
                    type="button"
                    className={`list-group-item list-group-item-action ${v.vendorId === selected ? "active" : ""}`}
                    onClick={() => setSelected(v.vendorId)}
                  >
                    <div className="d-flex justify-content-between align-items-start">
                      <div>
                        <div className="fw-semibold">{v.vendorName || "Unknown"}</div>
                        <small className={v.vendorId === selected ? "" : "text-muted"}>{v.vendorPhone || "no phone"}</small>
                      </div>
                      <div className="text-end">
                        {v.needsAttention > 0 && <span className="badge bg-warning text-dark ms-1">{v.needsAttention}</span>}
                        {v.unread > 0 && <span className="badge bg-primary ms-1">{v.unread}</span>}
                        {v.openRequisitions > 0 && <span className="badge bg-light text-dark border ms-1">{v.openRequisitions} open</span>}
                      </div>
                    </div>
                    {v.lastActivityAt && <small className={v.vendorId === selected ? "" : "text-muted"}>{when(v.lastActivityAt)}</small>}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* thread */}
          <div className="col-lg-8 col-xl-9">
            <div className="card">
              <div className="card-header d-flex justify-content-between align-items-center">
                <div>
                  <strong>{current ? current.vendorName : "Select a vendor"}</strong>
                  {current && current.vendorPhone && <span className="text-muted ms-2">{current.vendorPhone}</span>}
                </div>
                <button type="button" className="btn btn-sm btn-light" onClick={refresh}>
                  Refresh
                </button>
              </div>

              <div className="card-body" style={{ height: "60vh", overflowY: "auto", backgroundColor: "#f6f7f2" }}>
                {!selected && <p className="text-muted">Pick a vendor on the left. Anything waiting on you is listed first.</p>}
                {selected && loadingThread && <p className="text-muted">Loading…</p>}
                {selected && !loadingThread && thread && thread.items.length === 0 && (
                  <p className="text-muted">No messages with this vendor yet.</p>
                )}
                {selected &&
                  !loadingThread &&
                  thread &&
                  thread.items.map((item) =>
                    item.kind === "requisition" ? (
                      <RequisitionCard key={`r${item.id}`} item={item} act={act} />
                    ) : item.kind === "intake" ? (
                      <IntakeCard key={`i${item.id}`} item={item} act={act} vendors={vendors} unidentified={selected === UNIDENTIFIED_ID} />
                    ) : (
                      <MessageBubble key={`m${item.id}`} item={item} />
                    )
                  )}
                <div ref={bottomRef} />
              </div>

              <div className="card-footer">
                <div className="input-group">
                  <textarea
                    className="form-control"
                    rows={2}
                    placeholder={selected ? "Message this vendor…" : "Select a vendor first"}
                    value={reply}
                    disabled={!selected || sending}
                    onChange={(e) => setReply(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) handleSend();
                    }}
                  />
                  <button type="button" className="btn btn-outline-success" disabled={!selected || sending || !reply.trim()} onClick={handleSend}>
                    {sending ? "Sending…" : "Send"}
                  </button>
                </div>
                <small className="text-muted">Free text only reaches a vendor who has written in the last 24 hours; otherwise a requisition template is needed.</small>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// What we sent, as the vendor received it. When a picture went with the
// message the picture usually is the message - a requirement list, a photo
// of a plant - so it is shown, not described. A template send also says so:
// its wording is fixed at Meta's approval and cannot be edited here.
function MessageBubble({ item }) {
  const src = item.mediaUrl || (item.mediaPath ? `${baseUrl}${item.mediaPath}` : null);
  return (
    <div className="d-flex justify-content-end mb-2">
      <div className="p-2 rounded bg-success bg-opacity-10 border" style={{ maxWidth: "75%" }}>
        {src && (
          <a href={src} target="_blank" rel="noreferrer">
            <img src={src} alt="sent to the vendor" style={{ maxWidth: "280px", maxHeight: "280px", borderRadius: "4px" }} className="mb-2 d-block" />
          </a>
        )}
        {item.text && <div style={{ whiteSpace: "pre-wrap" }}>{item.text}</div>}
        <small className="text-muted">
          {when(item.at)} · <Badge status={item.status} />
          {item.templateName && <span className="ms-2">template <code>{item.templateName}</code></span>}
          {item.error && <span className="text-danger ms-2">{item.error}</span>}
        </small>
      </div>
    </div>
  );
}

function RequisitionCard({ item, act }) {
  const [editing, setEditing] = useState(false);
  const [text, setText] = useState(item.text);
  const canSend = item.status === "Draft" || item.status === "Failed";

  return (
    <div className="d-flex justify-content-end mb-2">
      <div className="card border-success" style={{ maxWidth: "80%", minWidth: "50%" }}>
        <div className="card-header py-1 d-flex justify-content-between align-items-center bg-success bg-opacity-10">
          <small>
            Requisition #{item.id}
            {item.requirementNumber && (
              <>
                {" "}· {item.requirementNumber} · {item.accountName}
              </>
            )}
          </small>
          <Badge status={item.status} />
        </div>
        <div className="card-body py-2">
          {editing ? (
            <textarea className="form-control mb-2" rows={6} value={text} onChange={(e) => setText(e.target.value)} />
          ) : (
            <div style={{ whiteSpace: "pre-wrap" }}>{item.text}</div>
          )}
          {item.note && <div className="alert alert-secondary py-1 px-2 mt-2 mb-0 small">{item.note}</div>}
          {item.error && <div className="text-danger small mt-1">{item.error}</div>}
          <small className="text-muted d-block mt-1">
            {when(item.at)}
            {item.wamid && <> · sent</>}
          </small>
          {canSend && (
            <div className="mt-2">
              {editing ? (
                <>
                  <button type="button" className="btn btn-sm btn-success me-2" onClick={() => act(() => approveRequisition(item.id, text), "Sent to vendor")}>
                    Send edited
                  </button>
                  <button type="button" className="btn btn-sm btn-light" onClick={() => setEditing(false)}>
                    Back
                  </button>
                </>
              ) : (
                <>
                  <button type="button" className="btn btn-sm btn-success me-2" onClick={() => act(() => approveRequisition(item.id), "Sent to vendor")}>
                    Approve &amp; send
                  </button>
                  <button type="button" className="btn btn-sm btn-light me-2" onClick={() => setEditing(true)}>
                    Edit
                  </button>
                  {item.status === "Draft" && (
                    <button type="button" className="btn btn-sm btn-outline-danger" onClick={() => act(() => cancelRequisition(item.id), "Cancelled")}>
                      Cancel
                    </button>
                  )}
                </>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// An entry from the MNB Rates app: who keyed it in says how much to trust it.
function appLabel(item) {
  const entry = (item.extraction && item.extraction.entry) || {};
  if (entry.kind === "vendor") return "Vendor's own entry (app)";
  const who = entry.submittedBy && entry.submittedBy.name;
  return who ? `Field visit by ${who} (app)` : "Field visit (app)";
}

function IntakeCard({ item, act, vendors = [], unidentified = false }) {
  const lines = (item.extraction && item.extraction.lines) || [];
  const [attributeTo, setAttributeTo] = useState("");
  const [edits, setEdits] = useState(() =>
    lines.map((l) => ({
      variantId: (l.binding && l.binding.variantId) || "",
      rate: l.rate === null || l.rate === undefined ? "" : String(l.rate),
      requisitionLineId: (l.binding && l.binding.requisitionLineId) || null,
      include: Boolean(l.binding && l.binding.variantId && l.rate !== null),
    }))
  );
  const pending = item.status === "Pending";

  const setEdit = (i, patch) => setEdits((prev) => prev.map((e, j) => (j === i ? { ...e, ...patch } : e)));

  const confirm = () => {
    const payload = {
      lines: edits
        .map((e, i) => (e.include && e.variantId && e.rate !== "" ? { variantId: e.variantId, rate: Number(e.rate), requisitionLineId: e.requisitionLineId, plantName: lines[i].plant_name, height: lines[i].height, bagSize: lines[i].bag_size } : null))
        .filter(Boolean),
    };
    if (!payload.lines.length) {
      errorToast("Tick at least one line with a variant and a rate");
      return;
    }
    if (unidentified) {
      if (!attributeTo) {
        errorToast("Pick which vendor this reply is from");
        return;
      }
      payload.vendorId = attributeTo;
    }
    act(() => confirmVendorRates(item.id, payload), "Rates logged to Zoho");
  };

  return (
    <div className="d-flex justify-content-start mb-2">
      <div className={`card ${pending ? "border-warning" : ""}`} style={{ maxWidth: "85%", minWidth: "50%" }}>
        <div className="card-header py-1 d-flex justify-content-between align-items-center">
          <small>
            {item.messageType === "image" ? "Photo" : item.messageType === "text" ? "Reply" : item.messageType === "app" ? appLabel(item) : item.messageType}
            {item.requisitionId && <> · answers requisition #{item.requisitionId}</>}
          </small>
          <Badge status={item.status} />
        </div>
        <div className="card-body py-2">
          {item.mediaPath && (
            <a href={item.mediaUrl || `${baseUrl}${item.mediaPath}`} target="_blank" rel="noreferrer">
              <img src={item.mediaUrl || `${baseUrl}${item.mediaPath}`} alt="vendor" style={{ maxWidth: "280px", maxHeight: "280px", borderRadius: "4px" }} className="mb-2 d-block" />
            </a>
          )}
          {item.extraMediaUrls && item.extraMediaUrls.length > 0 && (
            <div className="d-flex flex-wrap gap-1 mb-2">
              {item.extraMediaUrls.map((u) => (
                <a key={u} href={u} target="_blank" rel="noreferrer">
                  <img src={u} alt="more" style={{ width: 84, height: 84, objectFit: "cover", borderRadius: "4px" }} />
                </a>
              ))}
            </div>
          )}
          {item.text && <div style={{ whiteSpace: "pre-wrap" }}>{item.text}</div>}
          {item.error && <div className="text-danger small mt-1">{item.error}</div>}
          <small className="text-muted d-block mt-1">{when(item.at)}</small>

          {item.status === "NotARate" && <div className="alert alert-info py-1 px-2 mt-2 mb-0 small">No price in this message — it may need a reply.</div>}

          {item.loggedRates && item.loggedRates.length > 0 && (
            <div className="mt-2 small">
              <div className="text-muted">Logged to Zoho:</div>
              <ul className="mb-0 ps-3">
                {item.loggedRates.map((r) => (
                  <li key={r.rateId}>
                    {r.name} — ₹{r.rate}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {lines.length > 0 && (pending || item.status === "Partial") && (
            <div className="mt-2">
              {unidentified && (
                <div className="mb-2">
                  <label className="small text-muted d-block">This number is not on any vendor record. Log the rates as:</label>
                  <select className="form-control form-control-sm" style={{ maxWidth: 320 }} value={attributeTo} onChange={(e) => setAttributeTo(e.target.value)}>
                    <option value="">— pick the vendor —</option>
                    {vendors
                      .filter((v) => v.vendorId !== UNIDENTIFIED_ID)
                      .map((v) => (
                        <option key={v.vendorId} value={v.vendorId}>
                          {v.vendorName}
                        </option>
                      ))}
                  </select>
                  <small className="text-muted">Add the number to that vendor in Zoho afterwards so the next reply matches on its own.</small>
                </div>
              )}
              <div className="small text-muted mb-1">Confirm what to log. Each line needs a variant and a rate.</div>
              <div className="table-responsive">
                <table className="table table-sm mb-2 align-middle">
                  <thead>
                    <tr>
                      <th></th>
                      <th>Vendor wrote</th>
                      <th>Size</th>
                      <th>Variant</th>
                      <th style={{ width: 110 }}>Rate ₹</th>
                    </tr>
                  </thead>
                  <tbody>
                    {lines.map((l, i) => {
                      const b = l.binding || {};
                      return (
                        <tr key={i}>
                          <td>
                            <input type="checkbox" checked={edits[i].include} onChange={(e) => setEdit(i, { include: e.target.checked })} />
                          </td>
                          <td>
                            {l.plant_name}
                            {l.confidence === "low" && <span className="badge bg-warning text-dark ms-1">low confidence</span>}
                          </td>
                          <td>{[l.height, l.bag_size].filter(Boolean).join(", ") || "—"}</td>
                          <td>
                            {b.status === "bound" ? (
                              <span>{b.variantName}</span>
                            ) : (
                              <>
                                <select className="form-control form-control-sm" value={edits[i].variantId} onChange={(e) => setEdit(i, { variantId: e.target.value, include: true })}>
                                  <option value="">— pick —</option>
                                  {(b.candidates || []).map((c) => (
                                    <option key={c.id} value={c.id}>
                                      {c.name}
                                    </option>
                                  ))}
                                </select>
                                {b.note && <small className="text-muted d-block">{b.note}</small>}
                              </>
                            )}
                          </td>
                          <td>
                            <input type="number" className="form-control form-control-sm" value={edits[i].rate} onChange={(e) => setEdit(i, { rate: e.target.value })} />
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <button type="button" className="btn btn-sm btn-success me-2" onClick={confirm}>
                Confirm &amp; log to Zoho
              </button>
              <button type="button" className="btn btn-sm btn-outline-danger" onClick={() => act(() => rejectVendorRates(item.id, "rejected from inbox"), "Rejected")}>
                Reject
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default VendorInbox;
